import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { transliterate } from "transliteration";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import { Table, TableBody, TableCell, TableHeader, TableRow } from "../../components/ui/table";
import { Button } from "react-bootstrap";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faPlus, faRepeat, faTrash } from "@fortawesome/free-solid-svg-icons";
import { faCopy, faFaceSmile } from "@fortawesome/free-regular-svg-icons";
import { faTimes, faSearch } from "@fortawesome/free-solid-svg-icons";
import CustomPagination from "../../components/common/pagination";
import { Editor } from 'react-draft-wysiwyg';
import { EditorState, ContentState, Modifier } from 'draft-js';
import EmojiPicker from 'emoji-picker-react';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import useClipboard from "../../hooks/useClipboard";

// ─── Emoji extractor helper ──────────────────────────────────────────────────
const emojiRegex = /(\p{Emoji_Presentation}|\p{Extended_Pictographic})/gu;

// ─── Translate English → Hindi via MyMemory, then transliterate to Hinglish ──
const translateToHinglish = async (text) => {
    if (!text?.trim()) return '';
    try {
        const emojis = text.match(emojiRegex) || [];
        const withoutEmoji = text.replace(emojiRegex, '').trim();
        if (!withoutEmoji) return emojis.join(' ');
        const res = await fetch(
            `https://api.mymemory.translated.net/get?q=${encodeURIComponent(withoutEmoji)}&langpair=en|hi`
        );
        const data = await res.json();
        const hindi = data.responseData?.translatedText || withoutEmoji;
        const hinglish = transliterate(hindi);
        return (hinglish.trim() + (emojis.length ? ' ' + emojis.join(' ') : '')).trim();
    } catch {
        return transliterate(text);
    }
};

const editorStateFromText = (text) => {
    if (!text) return EditorState.createEmpty();
    return EditorState.createWithContent(ContentState.createFromText(text));
};

const toolbarOptions = { options: [] };

const NOTIFICATION_TYPES = [
    { value: 'regular', label: 'Regular' },
    { value: 'question', label: 'Question' },
];

// ─── Reusable Editor Block ───────────────────────────────────────────────────
const EditorBlock = ({
    label, required, hint, edState, onChange,
    showEmoji, setShowEmoji, emojiRef, editorDivRef,
    error, isHinglish = false, minHeight = "min-h-[80px]",
    isSubmitting, onEmojiInsert,
    onConvert, isConverting = false,
}) => (
    <div className="mb-4 relative">
        {/* Label row */}
        <div className="flex items-center gap-2 flex-wrap mb-2">
            <label className="block font-medium">
                {label}
                {required && <span className="text-red-500 pl-2 font-normal text-lg">*</span>}
            </label>
            {/* Convert button — only for Hinglish blocks */}
            {isHinglish && onConvert && (
                <button
                    type="button"
                    onClick={onConvert}
                    disabled={isConverting || isSubmitting}
                    className="ml-auto inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-semibold bg-orange-500 hover:bg-orange-600 active:bg-orange-700 text-white disabled:opacity-60 transition-colors"
                >
                    {isConverting ? (
                        <>
                            <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                            Converting...
                        </>
                    ) : (
                        '🔁 Convert to Hinglish'
                    )}
                </button>
            )}
        </div>


        {/* Editor */}
        <div
            className={`relative border rounded ${error ? 'border-red-500' : isHinglish ? 'border-orange-300 dark:border-orange-700' : 'border-gray-300 dark:border-gray-600'}`}
            ref={editorDivRef}
        >
            <Editor
                editorState={edState}
                wrapperClassName="demo-wrapper"
                editorClassName={`demo-editor px-2 m-0 border-0 ${minHeight} dark:text-gray-300 dark:bg-gray-800`}
                onEditorStateChange={onChange}
                toolbar={toolbarOptions}
                placeholder={isHinglish ? `Click "Convert to Hinglish" to auto-generate...` : `Enter ${label.toLowerCase()}`}
                readOnly={isSubmitting}
            />
            <div className="p-2 flex justify-end border-t border-gray-100 dark:border-gray-700">
                <button
                    type="button"
                    className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                    onClick={() => setShowEmoji(v => !v)}
                    disabled={isSubmitting}
                >
                    <FontAwesomeIcon icon={faFaceSmile} size="lg" />
                </button>
            </div>
            {showEmoji && (
                <div
                    ref={emojiRef}
                    className="absolute bottom-0 right-0 bg-white dark:bg-gray-800 shadow-lg rounded-lg z-50"
                    onMouseDown={e => e.preventDefault()}
                >
                    <EmojiPicker
                        onEmojiClick={emoji => { onEmojiInsert(emoji); setShowEmoji(false); }}
                        width={300} height={400}
                        previewConfig={{ showPreview: false }}
                        emojiStyle="native" emojiVersion="12.0"
                    />
                </div>
            )}
        </div>
        {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
    </div>
);

// ─── Main Component ──────────────────────────────────────────────────────────
const PushNotification = () => {
    const { copyToClipboard } = useClipboard();

    const [visible, setVisible] = useState(false);
    const [id, setId] = useState();
    const [loading, setLoading] = useState(true);
    const [filteredData, setFilteredData] = useState([]);
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [repeatLoading, setRepeatLoading] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedType, setSelectedType] = useState('');
    const [filterType, setFilterType] = useState('');
    const [isTypeFilterOpen, setIsTypeFilterOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(15);
    const [Items, setTotalItems] = useState(0);

    // ── Converting states ────────────────────────────────────────────────────
    const [isTitleConverting, setIsTitleConverting] = useState(false);
    const [isDescConverting, setIsDescConverting] = useState(false);

    // ── Editor states ────────────────────────────────────────────────────────
    const [titleState, setTitleState] = useState(EditorState.createEmpty());
    const [descState, setDescState] = useState(EditorState.createEmpty());
    const [enhiTitleState, setEnhiTitleState] = useState(EditorState.createEmpty());
    const [enhiDescState, setEnhiDescState] = useState(EditorState.createEmpty());

    // ── Emoji pickers ────────────────────────────────────────────────────────
    const [showTitleEmoji, setShowTitleEmoji] = useState(false);
    const [showDescEmoji, setShowDescEmoji] = useState(false);
    const [showEnhiTitleEmoji, setShowEnhiTitleEmoji] = useState(false);
    const [showEnhiDescEmoji, setShowEnhiDescEmoji] = useState(false);

    // ── Refs ─────────────────────────────────────────────────────────────────
    const titleEditorRef = useRef(null);
    const descEditorRef = useRef(null);
    const enhiTitleEditorRef = useRef(null);
    const enhiDescEditorRef = useRef(null);
    const titleEmojiRef = useRef(null);
    const descEmojiRef = useRef(null);
    const enhiTitleEmojiRef = useRef(null);
    const enhiDescEmojiRef = useRef(null);
    const typeFilterRef = useRef(null);
    const searchContainerRef = useRef(null);

    // ── Close dropdowns/emojis on outside click ───────────────────────────────
    useEffect(() => {
        const handler = (e) => {
            if (titleEmojiRef.current && !titleEmojiRef.current.contains(e.target)) setShowTitleEmoji(false);
            if (descEmojiRef.current && !descEmojiRef.current.contains(e.target)) setShowDescEmoji(false);
            if (enhiTitleEmojiRef.current && !enhiTitleEmojiRef.current.contains(e.target)) setShowEnhiTitleEmoji(false);
            if (enhiDescEmojiRef.current && !enhiDescEmojiRef.current.contains(e.target)) setShowEnhiDescEmoji(false);
            if (typeFilterRef.current && !typeFilterRef.current.contains(e.target)) setIsTypeFilterOpen(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    // ── Convert Title to Hinglish (on button click) ───────────────────────────
    const handleConvertTitle = async () => {
        const plain = titleState.getCurrentContent().getPlainText().trim();
        if (!plain) { toast.warning("Please enter a title first."); return; }
        setIsTitleConverting(true);
        try {
            const result = await translateToHinglish(plain);
            setEnhiTitleState(editorStateFromText(result));
            if (errors.enhiTitle) setErrors(p => { const e = { ...p }; delete e.enhiTitle; return e; });
        } catch {
            toast.error("Failed to convert title. Please try again.");
        } finally {
            setIsTitleConverting(false);
        }
    };

    // ── Convert Description to Hinglish (on button click) ─────────────────────
    const handleConvertDesc = async () => {
        const plain = descState.getCurrentContent().getPlainText().trim();
        if (!plain) { toast.warning("Please enter a description first."); return; }
        setIsDescConverting(true);
        try {
            const result = await translateToHinglish(plain);
            setEnhiDescState(editorStateFromText(result));
            if (errors.enhiDescription) setErrors(p => { const e = { ...p }; delete e.enhiDescription; return e; });
        } catch {
            toast.error("Failed to convert description. Please try again.");
        } finally {
            setIsDescConverting(false);
        }
    };

    // ── API ───────────────────────────────────────────────────────────────────
    const getData = async (page = currentPage, search = searchTerm, typeFilter = filterType) => {
        try {
            setLoading(true);
            const response = await axios.post('https://api.lolcards.link/api/notification/read', {
                modelType: "push",
                page,
                limit: itemsPerPage,
                search: search.trim(),
                type: typeFilter || undefined,
            });
            setFilteredData(response.data.data);
            setCurrentPage(response.data.pagination.currentPage);
            setTotalItems(response.data.pagination.totalItems);
        } catch (err) {
            console.error(err);
            toast.error("Failed to fetch data.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { getData(currentPage, searchTerm, filterType); }, [itemsPerPage]);

    // ── Search ────────────────────────────────────────────────────────────────
    const handleSearch = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        if (value.trim() === '') { setCurrentPage(1); getData(1, '', filterType); }
    };
    const handleSearchSubmit = (e) => { e.preventDefault(); setCurrentPage(1); getData(1, searchTerm.trim(), filterType); };
    const handleClearSearch = () => { setSearchTerm(''); setCurrentPage(1); getData(1, '', filterType); };

    // ── Form helpers ──────────────────────────────────────────────────────────
    const resetForm = () => {
        setTitleState(EditorState.createEmpty());
        setDescState(EditorState.createEmpty());
        setEnhiTitleState(EditorState.createEmpty());
        setEnhiDescState(EditorState.createEmpty());
        setSelectedType('');
        setId(null);
        setErrors({});
        setVisible(false);
    };

    const handleEdit = (notification) => {
        if (isSubmitting) return;
        setTitleState(editorStateFromText(notification.Title || ''));
        setDescState(editorStateFromText(notification.Description || ''));
        setEnhiTitleState(editorStateFromText(notification.enhiTitle || ''));
        setEnhiDescState(editorStateFromText(notification.enhiDescription || ''));
        setSelectedType(notification.type || '');
        setId(notification._id);
        setErrors({});
        setVisible(true);
    };

    // ── Editor change handlers ────────────────────────────────────────────────
    const handleTitleChange = (state) => {
        setTitleState(state);
        if (errors.title) setErrors(p => { const e = { ...p }; delete e.title; return e; });
    };
    const handleDescChange = (state) => {
        setDescState(state);
        if (errors.description) setErrors(p => { const e = { ...p }; delete e.description; return e; });
    };

    // ── Emoji insert ──────────────────────────────────────────────────────────
    const insertEmoji = (emoji, currentState, setState, ref) => {
        const cs = currentState.getCurrentContent();
        const sel = currentState.getSelection();
        const newCs = Modifier.replaceText(cs, sel, emoji.emoji);
        const newState = EditorState.push(currentState, newCs, 'insert-characters');
        const newSel = sel.merge({
            anchorOffset: sel.getAnchorOffset() + emoji.emoji.length,
            focusOffset: sel.getFocusOffset() + emoji.emoji.length,
        });
        setState(EditorState.forceSelection(newState, newSel));
        if (ref?.current) setTimeout(() => ref.current.focus(), 0);
    };

    // ── Validate ──────────────────────────────────────────────────────────────
    const validate = () => {
        const errs = {};
        if (!titleState.getCurrentContent().getPlainText().trim())
            errs.title = 'Notification Title is required';
        if (!descState.getCurrentContent().getPlainText().trim())
            errs.description = 'Notification Description is required';
        if (!selectedType)
            errs.type = 'Please select a notification type';
        if (!enhiTitleState.getCurrentContent().getPlainText().trim())
            errs.enhiTitle = 'Hinglish Title is required — click "Convert to Hinglish" to generate it';
        if (!enhiDescState.getCurrentContent().getPlainText().trim())
            errs.enhiDescription = 'Hinglish Description is required — click "Convert to Hinglish" to generate it';
        return errs;
    };

    // ── Submit ────────────────────────────────────────────────────────────────
    const handleSubmit = async (e) => {
        e.preventDefault();
        const validationErrors = validate();
        if (Object.keys(validationErrors).length > 0) { setErrors(validationErrors); return; }
        try {
            setIsSubmitting(true);
            const payload = {
                Title: titleState.getCurrentContent().getPlainText(),
                Description: descState.getCurrentContent().getPlainText(),
                enhiTitle: enhiTitleState.getCurrentContent().getPlainText(),
                enhiDescription: enhiDescState.getCurrentContent().getPlainText(),
                type: selectedType,
                modelType: 'push',
            };
            const request = id
                ? axios.patch(`https://api.lolcards.link/api/notification/update/${id}`, payload)
                : axios.post('https://api.lolcards.link/api/notification/create', payload);
            const res = await request;
            toast.success(res.data.message);
            resetForm();
            getData();
        } catch (err) {
            console.error(err);
            toast.error("An error occurred. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    // ── Repeat ────────────────────────────────────────────────────────────────
    const handleRepeat = async (notification) => {
        try {
            setRepeatLoading(prev => ({ ...prev, [notification._id]: true }));
            const enhiTitle = notification.enhiTitle || await translateToHinglish(notification.Title || '');
            const enhiDescription = notification.enhiDescription || await translateToHinglish(notification.Description || '');
            await axios.post('https://api.lolcards.link/api/notification/create', {
                Title: notification.Title,
                Description: notification.Description,
                enhiTitle,
                enhiDescription,
                type: notification.type || 'regular',
                modelType: 'push',
            });
            toast.success("Notification repeated successfully!");
            getData(currentPage);
        } catch (err) {
            console.error(err);
            toast.error("Failed to repeat notification.");
        } finally {
            setRepeatLoading(prev => ({ ...prev, [notification._id]: false }));
        }
    };

    // ── Delete ────────────────────────────────────────────────────────────────
    const handleDelete = async (notifId) => {
        if (isSubmitting || !window.confirm("Are you sure you want to delete this Push notification?")) return;
        try {
            setIsSubmitting(true);
            const response = await axios.delete(
                `https://api.lolcards.link/api/notification/delete/${notifId}`,
                { data: { modelType: "push" } }
            );
            toast.success(response.data.message);
            getData(currentPage);
        } catch (err) {
            console.error(err);
            toast.error("An error occurred. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const totalPages = Math.ceil(Items / itemsPerPage);
    const indexOfFirstItem = (currentPage - 1) * itemsPerPage;
    const currentItems = filteredData;

    if (loading) return (
        <div style={{ height: '80vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <div className="border p-4 flex items-center space-x-2 rounded-md">
                <div className="w-10 h-10 border-2 border-gray-300 rounded-full animate-spin" style={{ borderTop: "2px solid #FA4B56" }}></div>
            </div>
        </div>
    );

    return (
        <div>
            <PageBreadcrumb pageTitle="Push-Notification" />
            <div className="space-y-6 sticky left-0">
                <div className="rounded-2xl overflow-auto border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]" style={{ minHeight: "600px" }}>

                    {/* ── Card Header ── */}
                    <div className="px-6 pt-5">
                        <div className="flex justify-between items-center px-4 py-3 mt-4">
                            <div className="flex gap-4 items-center flex-wrap">
                                {/* Search */}
                                <div ref={searchContainerRef} className="relative">
                                    <form onSubmit={handleSearchSubmit}>
                                        <div className="relative flex align-middle justify-center">
                                            <span className="absolute -translate-y-1/2 pointer-events-none left-4 top-1/2">
                                                <svg className="fill-gray-500 dark:fill-gray-400" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path fillRule="evenodd" clipRule="evenodd" d="M3.04175 9.37363C3.04175 5.87693 5.87711 3.04199 9.37508 3.04199C12.8731 3.04199 15.7084 5.87693 15.7084 9.37363C15.7084 12.8703 12.8731 15.7053 9.37508 15.7053C5.87711 15.7053 3.04175 12.8703 3.04175 9.37363ZM9.37508 1.54199C5.04902 1.54199 1.54175 5.04817 1.54175 9.37363C1.54175 13.6991 5.04902 17.2053 9.37508 17.2053C11.2674 17.2053 13.003 16.5344 14.357 15.4176L17.177 18.238C17.4699 18.5309 17.9448 18.5309 18.2377 18.238C18.5306 17.9451 18.5306 17.4703 18.2377 17.1774L15.418 14.3573C16.5365 13.0033 17.2084 11.2669 17.2084 9.37363C17.2084 5.04817 13.7011 1.54199 9.37508 1.54199Z" fill="" />
                                                </svg>
                                            </span>
                                            <input
                                                type="text"
                                                placeholder="Search by description..."
                                                value={searchTerm}
                                                onChange={handleSearch}
                                                className="dark:bg-dark-900 h-11 w-full rounded-lg border border-gray-200 bg-transparent py-2.5 pl-12 pr-14 text-md text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-800 dark:bg-gray-900 dark:bg-white/[0.03] dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800 xl:w-[300px]"
                                            />
                                            <div className="absolute right-2.5 top-1/2 -translate-y-1/2 flex items-center gap-1">
                                                {searchTerm && (
                                                    <button type="button" onClick={handleClearSearch} className="inline-flex items-center px-[7px] py-[4.5px] text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300">
                                                        <FontAwesomeIcon icon={faTimes} />
                                                    </button>
                                                )}
                                                <button type="submit" className="inline-flex items-center px-[7px] py-[4.5px] text-xs text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400">
                                                    <FontAwesomeIcon icon={faSearch} />
                                                </button>
                                            </div>
                                        </div>
                                    </form>
                                </div>

                                {/* Type filter */}
                                <div className="relative inline-block w-48" ref={typeFilterRef}>
                                    <button
                                        className="w-full flex items-center justify-between px-4 py-2 bg-white dark:border-gray-800 border rounded-md text-gray-600 hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-300"
                                        onClick={() => setIsTypeFilterOpen(!isTypeFilterOpen)}
                                        type="button"
                                    >
                                        <span>{filterType === '' ? 'All Types' : NOTIFICATION_TYPES.find(t => t.value === filterType)?.label}</span>
                                        <FontAwesomeIcon icon={faChevronDown} />
                                    </button>
                                    {isTypeFilterOpen && (
                                        <div className="absolute w-full mt-2 bg-white shadow-lg rounded-lg border dark:bg-gray-800 z-50 px-1">
                                            <button
                                                onClick={() => { setFilterType(''); setIsTypeFilterOpen(false); setCurrentPage(1); getData(1, searchTerm, ''); }}
                                                className={`flex items-center w-full px-4 py-2 my-1 text-left text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/10 rounded-md ${filterType === '' ? 'bg-gray-100 dark:bg-white/10' : ''}`}
                                            >
                                                All Types
                                            </button>
                                            {NOTIFICATION_TYPES.map(type => (
                                                <button
                                                    key={type.value}
                                                    onClick={() => { setFilterType(type.value); setIsTypeFilterOpen(false); setCurrentPage(1); getData(1, searchTerm, type.value); }}
                                                    className={`flex items-center w-full px-4 py-2 my-1 text-left text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/10 rounded-md ${filterType === type.value ? 'bg-gray-100 dark:bg-white/10' : ''}`}
                                                >
                                                    {type.label}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <Button
                                onClick={() => { resetForm(); setVisible(true); }}
                                className="rounded-md border-0 shadow-md px-4 py-2 text-white"
                                style={{ background: "#FA4B56" }}
                            >
                                <FontAwesomeIcon icon={faPlus} className='pe-2' /> Add Push Notification
                            </Button>
                        </div>
                    </div>

                    {/* ── Table ── */}
                    <div className="p-4 border-gray-100 dark:border-gray-800 sm:p-6 overflow-auto">
                        <div className="space-y-6 rounded-lg xl:border dark:border-gray-800">
                            <Table>
                                <TableHeader className="border-b border-gray-100 dark:border-white/[0.05]">
                                    <TableRow>
                                        <TableCell isHeader className="py-4 font-medium text-gray-500 px-2 border-r border-gray-200 dark:border-gray-700">Index</TableCell>
                                        <TableCell isHeader className="py-4 font-medium text-gray-500 px-2 border-r border-gray-200 dark:border-gray-700">Title (English)</TableCell>
                                        <TableCell isHeader className="py-4 font-medium text-gray-500 px-2 border-r border-gray-200 dark:border-gray-700">Title (Hinglish)</TableCell>
                                        <TableCell isHeader className="py-4 font-medium text-gray-500 px-2 border-r border-gray-200 dark:border-gray-700">Description (English)</TableCell>
                                        <TableCell isHeader className="py-4 font-medium text-gray-500 px-2 border-r border-gray-200 dark:border-gray-700">Description (Hinglish)</TableCell>
                                        <TableCell isHeader className="py-4 font-medium text-gray-500 px-2 border-r border-gray-200 dark:border-gray-700">Type</TableCell>
                                        <TableCell isHeader className="py-4 font-medium text-gray-500 px-2 border-r border-gray-200 dark:border-gray-700">Actions</TableCell>
                                    </TableRow>
                                </TableHeader>
                                <TableBody className="divide-y divide-gray-100 dark:divide-white/[0.05] text-center">
                                    {currentItems.length > 0 ? (
                                        currentItems.map((notification, index) => (
                                            <TableRow key={notification._id}>
                                                <TableCell className="text-center px-2 border-r border-gray-200 dark:border-gray-700 dark:text-gray-400">
                                                    {indexOfFirstItem + index + 1}
                                                </TableCell>
                                                <TableCell className="py-3 px-2 border-r border-gray-200 dark:border-gray-700 dark:text-gray-400">
                                                    <div className="flex items-center justify-center gap-2">
                                                        <span>{notification.Title}</span>
                                                        <button className="text-gray-400 hover:text-gray-600 shrink-0" onClick={() => copyToClipboard(notification.Title)} title="Copy English title">
                                                            <FontAwesomeIcon icon={faCopy} />
                                                        </button>
                                                    </div>
                                                </TableCell>
                                                <TableCell className="py-3 px-2 border-r border-gray-200 dark:border-gray-700">
                                                    <div className="flex items-center justify-center gap-2">
                                                        <span className="">
                                                            {notification.enhiTitle || <span className="text-gray-400 text-xs italic">—</span>}
                                                        </span>
                                                        {notification.enhiTitle && (
                                                            <button className="text-gray-400 hover:text-gray-600 shrink-0" onClick={() => copyToClipboard(notification.enhiTitle)} title="Copy Hinglish title">
                                                                <FontAwesomeIcon icon={faCopy} />
                                                            </button>
                                                        )}
                                                    </div>
                                                </TableCell>
                                                <TableCell className="py-3 px-2 border-r border-gray-200 dark:border-gray-700 dark:text-gray-400">
                                                    <div className="flex items-center justify-center gap-2">
                                                        <span>{notification.Description}</span>
                                                        <button className="text-gray-400 hover:text-gray-600 shrink-0" onClick={() => copyToClipboard(notification.Description)} title="Copy English description">
                                                            <FontAwesomeIcon icon={faCopy} />
                                                        </button>
                                                    </div>
                                                </TableCell>
                                                <TableCell className="py-3 px-2 border-r border-gray-200 dark:border-gray-700">
                                                    <div className="flex items-center justify-center gap-2">
                                                        <span className="">
                                                            {notification.enhiDescription || <span className="text-gray-400 text-xs italic">—</span>}
                                                        </span>
                                                        {notification.enhiDescription && (
                                                            <button className="text-gray-400 hover:text-gray-600 shrink-0" onClick={() => copyToClipboard(notification.enhiDescription)} title="Copy Hinglish description">
                                                                <FontAwesomeIcon icon={faCopy} />
                                                            </button>
                                                        )}
                                                    </div>
                                                </TableCell>
                                                <TableCell className="py-3 px-2 border-r border-gray-200 dark:border-gray-700 dark:text-gray-400">
                                                    <span className={`px-2 py-1 rounded-full text-xs font-semibold capitalize ${notification.type === 'question' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300' : 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'}`}>
                                                        {notification.type || 'regular'}
                                                    </span>
                                                </TableCell>
                                                <TableCell className="py-3 px-2 border-r border-gray-200 dark:border-gray-700 dark:text-gray-400">
                                                    <div className="flex align-middle justify-center gap-4">
                                                        <button
                                                            style={{ color: "#0385C3" }}
                                                            onClick={() => handleRepeat(notification)}
                                                            disabled={repeatLoading[notification._id]}
                                                            title="Repeat this notification"
                                                        >
                                                            {repeatLoading[notification._id]
                                                                ? <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                                                                : <FontAwesomeIcon icon={faRepeat} className="text-lg" />
                                                            }
                                                        </button>
                                                        <button className="text-red-600" onClick={() => handleDelete(notification._id)}>
                                                            <FontAwesomeIcon icon={faTrash} className="text-lg" />
                                                        </button>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={7} className="text-center pt-5 pb-4 dark:text-gray-400">No Data Found</td>
                                        </tr>
                                    )}
                                </TableBody>
                            </Table>
                        </div>
                    </div>
                </div>
            </div>

            {totalPages > 0 && (
                <CustomPagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={(page) => getData(page, searchTerm, filterType)}
                    itemsPerPage={itemsPerPage}
                    totalItems={Items}
                />
            )}

            {/* ── Modal ── */}
            {visible && (
                <div className="fixed inset-0 z-99999 flex items-center justify-center">
                    <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => !isSubmitting && resetForm()}></div>
                    <div className="relative bg-white rounded-lg w-full max-w-lg mx-4 dark:bg-gray-800 dark:text-gray-300 max-h-[90vh] overflow-y-auto">
                        <div className="px-6 py-4 border-b sticky top-0 bg-white dark:bg-gray-800 z-10">
                            <h3 className="text-xl font-semibold">{id ? "Edit Push Notification" : "Push Notification"}</h3>
                        </div>
                        <div className="px-6 py-4">
                            <form onSubmit={handleSubmit}>

                                {/* ── Notification Type ── */}
                                <div className="mb-4">
                                    <label className="block font-medium mb-2">
                                        Notification Type
                                        <span className="text-red-500 pl-2 font-normal text-lg">*</span>
                                    </label>
                                    <select
                                        value={selectedType}
                                        onChange={(e) => {
                                            setSelectedType(e.target.value);
                                            if (errors.type) setErrors(p => { const er = { ...p }; delete er.type; return er; });
                                        }}
                                        disabled={isSubmitting}
                                        className={`w-full h-11 rounded-lg border px-4 text-md bg-white text-gray-800 focus:outline-none focus:ring focus:ring-brand-500/10 dark:bg-gray-800 dark:text-white/90 ${errors.type ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}`}
                                    >
                                        <option value="">Select Type</option>
                                        {NOTIFICATION_TYPES.map(t => (
                                            <option key={t.value} value={t.value}>{t.label}</option>
                                        ))}
                                    </select>
                                    {errors.type && <p className="text-red-500 text-sm mt-1">{errors.type}</p>}
                                </div>

                                {/* ── English Title ── */}
                                <EditorBlock
                                    label="Push Notification Title"
                                    required
                                    hint='Type your English title, then click "Convert to Hinglish" in the field below.'
                                    edState={titleState}
                                    onChange={handleTitleChange}
                                    showEmoji={showTitleEmoji}
                                    setShowEmoji={setShowTitleEmoji}
                                    emojiRef={titleEmojiRef}
                                    editorDivRef={titleEditorRef}
                                    error={errors.title}
                                    minHeight="min-h-[40px]"
                                    isSubmitting={isSubmitting}
                                    onEmojiInsert={(emoji) => insertEmoji(emoji, titleState, setTitleState, titleEditorRef)}
                                />

                                {/* ── Hinglish Title ── */}
                                <EditorBlock
                                    label="Push Notification Title (Hinglish)"
                                    required
                                    edState={enhiTitleState}
                                    onChange={(state) => {
                                        setEnhiTitleState(state);
                                        if (errors.enhiTitle) setErrors(p => { const e = { ...p }; delete e.enhiTitle; return e; });
                                    }}
                                    showEmoji={showEnhiTitleEmoji}
                                    setShowEmoji={setShowEnhiTitleEmoji}
                                    emojiRef={enhiTitleEmojiRef}
                                    editorDivRef={enhiTitleEditorRef}
                                    isHinglish
                                    minHeight="min-h-[40px]"
                                    isSubmitting={isSubmitting}
                                    error={errors.enhiTitle}
                                    onConvert={handleConvertTitle}
                                    isConverting={isTitleConverting}
                                    onEmojiInsert={(emoji) => insertEmoji(emoji, enhiTitleState, setEnhiTitleState, enhiTitleEditorRef)}
                                />

                                {/* ── English Description ── */}
                                <EditorBlock
                                    label="Push Notification Description"
                                    required
                                    hint='Type your English description, then click "Convert to Hinglish" in the field below.'
                                    edState={descState}
                                    onChange={handleDescChange}
                                    showEmoji={showDescEmoji}
                                    setShowEmoji={setShowDescEmoji}
                                    emojiRef={descEmojiRef}
                                    editorDivRef={descEditorRef}
                                    error={errors.description}
                                    isSubmitting={isSubmitting}
                                    onEmojiInsert={(emoji) => insertEmoji(emoji, descState, setDescState, descEditorRef)}
                                />

                                {/* ── Hinglish Description ── */}
                                <EditorBlock
                                    label="Push Notification Description (Hinglish)"
                                    required
                                    edState={enhiDescState}
                                    onChange={(state) => {
                                        setEnhiDescState(state);
                                        if (errors.enhiDescription) setErrors(p => { const e = { ...p }; delete e.enhiDescription; return e; });
                                    }}
                                    showEmoji={showEnhiDescEmoji}
                                    setShowEmoji={setShowEnhiDescEmoji}
                                    emojiRef={enhiDescEmojiRef}
                                    editorDivRef={enhiDescEditorRef}
                                    isHinglish
                                    isSubmitting={isSubmitting}
                                    error={errors.enhiDescription}
                                    onConvert={handleConvertDesc}
                                    isConverting={isDescConverting}
                                    onEmojiInsert={(emoji) => insertEmoji(emoji, enhiDescState, setEnhiDescState, enhiDescEditorRef)}
                                />

                                {/* ── Action Buttons ── */}
                                <div className="flex gap-4 mt-4">
                                    <button
                                        type="button"
                                        onClick={resetForm}
                                        disabled={isSubmitting}
                                        className="w-1/2 py-2 px-4 bg-gray-100 text-black rounded-lg hover:bg-gray-200 transition-colors duration-200"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className="w-1/2 py-2 px-4 text-white rounded-lg transition-colors duration-200"
                                        style={{ backgroundColor: "#FA4B56" }}
                                    >
                                        {isSubmitting
                                            ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto"></div>
                                            : id ? 'Update' : 'Submit'
                                        }
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            <ToastContainer position="top-center" className="!z-[99999]" />
        </div>
    );
};

export default PushNotification;